'use client';

import { ChevronRight, type LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@data-delivery/d3s';

export type MenuItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  isActive?: boolean;
  description?: string;
  /** Optional router state injected when this item is used as a breadcrumb link. */
  linkState?: Record<string, unknown>;
  items?: Array<{
    title: string;
    url: string;
    description?: string;
    /** Optional router state injected when this sub-item is used as a breadcrumb link. */
    linkState?: Record<string, unknown>;
  }>;
};

export function NavMain({ items }: { items: MenuItem[] }) {
  const location = useLocation();

  const isItemActive = (url: string) => {
    if (url === '#') {
      return false;
    }

    return location.pathname === url || location.pathname.startsWith(url + '/');
  };

  const hasActiveSubItem = (item: MenuItem) =>
    item.items?.some(subItem => isItemActive(subItem.url)) || false;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Menu</SidebarGroupLabel>
      <SidebarMenu>
        {items.map(item => {
          const itemIsActive = isItemActive(item.url) || hasActiveSubItem(item);

          return (
            <Collapsible key={item.title} asChild defaultOpen={item.isActive || itemIsActive}>
              <SidebarMenuItem>
                {item.url === '#' && item.items?.length ? (
                  <>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip={item.title} isActive={itemIsActive}>
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuAction className='data-[state=open]:rotate-90'>
                        <ChevronRight />
                        <span className='sr-only'>Toggle</span>
                      </SidebarMenuAction>
                    </CollapsibleTrigger>
                  </>
                ) : item.url === '#' ? (
                  <SidebarMenuButton tooltip={item.title} isActive={itemIsActive}>
                    <item.icon />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton asChild tooltip={item.title} isActive={itemIsActive}>
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                )}
                {item.items?.length && item.url !== '#' ? (
                  <>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuAction className='data-[state=open]:rotate-90'>
                        <ChevronRight />
                        <span className='sr-only'>Toggle</span>
                      </SidebarMenuAction>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {item.items?.map(subItem => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton asChild isActive={isItemActive(subItem.url)}>
                              <Link to={subItem.url}>
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </>
                ) : null}
                {item.items?.length && item.url === '#' ? (
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items?.map(subItem => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton asChild isActive={isItemActive(subItem.url)}>
                            <Link to={subItem.url}>
                              <span>{subItem.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                ) : null}
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
